import MuiDialog, { type DialogProps } from '@mui/material/Dialog';
import { Box, DialogActions, DialogContent, DialogTitle, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useId } from 'react';

export type ModalProps = DialogProps & {
  dialogTitle?: React.ReactNode;
  headerLeft?: React.ReactNode;
  actions?: React.ReactNode;
  children: React.ReactNode;
  dismissible?: boolean;
  hideCloseButton?: boolean;
};

function DialogHeader({
  id,
  dialogTitle,
  headerLeft,
  onClose,
  hideCloseButton,
}: {
  id?: string;
  dialogTitle: React.ReactNode;
  headerLeft?: React.ReactNode;
  onClose: () => void;
  hideCloseButton?: boolean;
}) {
  let titlePadding = 3;
  let titlePaddingBottom = 1;
  if (!dialogTitle) {
    titlePadding = 0;
    titlePaddingBottom = 0;
  }
  return (
    <>
      <DialogTitle
        id={id}
        component="div"
        sx={{
          position: 'relative',
          p: titlePadding,
          pb: titlePaddingBottom,
          minHeight: 48,
        }}
      >
        {headerLeft ? (
          <Box
            sx={{
              position: 'absolute',
              left: 8,
              top: 8,
              zIndex: 1,
            }}
          >
            {headerLeft}
          </Box>
        ) : null}
        {dialogTitle ? dialogTitle : null}
        {!hideCloseButton && (
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={(theme) => ({
              position: 'absolute',
              right: 8,
              top: 8,
              color: theme.palette.grey[500],
            })}
          >
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>
    </>
  );
}

export default function Modal({
  dismissible = true,
  dialogTitle,
  headerLeft,
  actions,
  children,
  hideCloseButton,
  open,
  onClose,
  fullWidth,
  maxWidth,
  ...rest
}: ModalProps) {
  const titleId = useId();
  let labelId: string | undefined;
  if (dialogTitle) {
    labelId = titleId;
  }

  const handleClose = (event: object, reason: 'backdropClick' | 'escapeKeyDown') => {
    if (!dismissible && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
      return;
    }
    if (onClose) {
      (onClose as (event: object, reason: string) => void)(event, reason);
    }
  };

  return (
    <>
      <MuiDialog
        {...rest}
        onClose={handleClose}
        open={open}
        aria-labelledby={labelId}
        fullWidth={fullWidth ?? true}
        maxWidth={maxWidth || 'md'}
      >
        <DialogHeader
          id={labelId}
          dialogTitle={dialogTitle}
          headerLeft={headerLeft}
          onClose={onClose as () => void}
          hideCloseButton={hideCloseButton}
        />
        <DialogContent sx={{ p: 3, pt: dialogTitle || headerLeft ? 1 : 0 }}>
          <Box sx={{ backgroundColor: 'transparent' }}>{children}</Box>
        </DialogContent>
        {actions && <DialogActions sx={{ p: 3, pt: 0 }}>{actions}</DialogActions>}
      </MuiDialog>
    </>
  );
}
